import * as Y from "yjs";

/**
 * Minimal central-relay sync provider (Google-Docs-style transport).
 *
 * It connects a Y.Doc to any broadcast WebSocket relay: on connect it sends the
 * full Yjs state, then streams incremental document updates both ways. Remote
 * updates are applied with this provider as the transaction origin so they are
 * never echoed back, avoiding feedback loops.
 *
 * This is intentionally dependency-free (native WebSocket + yjs only) and is a
 * prototype of the transport. Pairing it with a granular CRDT data model — one
 * Y.Map per entity and a Y.Text per spec document — is what shrinks each update
 * to a character/field-level delta and unlocks true concurrent editing. See
 * PLAN.md ("Real-time sync architecture") for the upgrade path to the full
 * y-websocket protocol with awareness-based presence and cursors.
 */
export class WebsocketSyncProvider {
  private socket: WebSocket | null = null;
  private subscribed = false;
  private reconnectTimer: number | null = null;
  private disposed = false;

  private readonly onDocUpdate = (update: Uint8Array, origin: unknown): void => {
    if (origin === this) return; // don't send back what we just received
    const socket = this.socket;
    if (socket && socket.readyState === WebSocket.OPEN) {
      socket.send(update);
    }
  };

  constructor(
    private readonly url: string,
    private readonly room: string,
    private readonly doc: Y.Doc,
  ) {
    this.connect();
  }

  private connect(): void {
    if (this.disposed) return;
    try {
      const separator = this.url.includes("?") ? "&" : "?";
      const socket = new WebSocket(`${this.url}${separator}room=${encodeURIComponent(this.room)}`);
      socket.binaryType = "arraybuffer";
      this.socket = socket;

      socket.onopen = () => {
        socket.send(Y.encodeStateAsUpdate(this.doc));
        if (!this.subscribed) {
          this.doc.on("update", this.onDocUpdate);
          this.subscribed = true;
        }
      };

      socket.onmessage = (event: MessageEvent) => {
        if (!(event.data instanceof ArrayBuffer)) return;
        Y.applyUpdate(this.doc, new Uint8Array(event.data), this);
      };

      socket.onclose = () => {
        this.teardownSocket();
        this.scheduleReconnect();
      };

      socket.onerror = () => {
        socket.close();
      };
    } catch {
      this.socket = null;
      this.scheduleReconnect();
    }
  }

  private scheduleReconnect(): void {
    if (this.disposed || this.reconnectTimer !== null) return;
    this.reconnectTimer = window.setTimeout(() => {
      this.reconnectTimer = null;
      this.connect();
    }, 2000);
  }

  private teardownSocket(): void {
    if (this.subscribed) {
      this.doc.off("update", this.onDocUpdate);
      this.subscribed = false;
    }
  }

  destroy(): void {
    this.disposed = true;
    if (this.reconnectTimer !== null) {
      window.clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }
    this.teardownSocket();
    if (this.socket) {
      this.socket.onclose = null;
      this.socket.close();
      this.socket = null;
    }
  }
}
